/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow
import type { Element } from 'react';

import classNames from 'classnames';
import React from 'react';
import SortableTable from '#components/support/table/SortableTable/index.jsx';
import authorColumn from '#components/pages/Models/Dashboard/ModelsTable/columns/authorColumn.jsx';
import modelColumn from '#components/pages/Models/Dashboard/ModelsTable/columns/modelColumn.jsx';
import projectColumn from '#components/pages/Models/Dashboard/ModelsTable/columns/projectColumn.jsx';
import levelColumn from '#components/pages/Models/Dashboard/ModelsTable/columns/levelColumn.jsx';
import selColumn from '#components/pages/Models/Dashboard/ModelsTable/columns/selColumn.jsx';
import sulColumn from '#components/pages/Models/Dashboard/ModelsTable/columns/sulColumn.jsx';
import createdDateColumn from '#components/pages/Models/Dashboard/ModelsTable/columns/createdDateColumn.jsx';
import updatedDateColumn from '#components/pages/Models/Dashboard/ModelsTable/columns/updatedDateColumn.jsx';
import statusColumn from '#components/pages/Models/Dashboard/ModelsTable/columns/statusColumn/index.jsx';
import actionColumn from '#components/pages/Models/Dashboard/ModelsTable/columns/actionColumn/index.jsx';
import { highlight, modelsTable } from './ModelsTable.module.scss';

type Model = {
  name: string,
  updatedAt: string,
  createdAt: string,
  togglingArchive?: boolean,
  recentlyCreated?: boolean,
  currentRun: {
    sel: number,
    status: string,
    updatedAt: string,
    site: {
      name: string,
    },
  },
  createdBy: {
    firstName: 'string',
    lastName: 'string',
  },
};

// Convert react-table v6 columns to TanStack Table v8 format
const convertColumn = (oldColumn: any) => {
  const { id, accessor, Header, Cell, width, sortable = true, additionalClasses = [] } = oldColumn;

  return {
    id,
    // Handle both function and string accessors
    accessorKey: typeof accessor === 'string' ? accessor : undefined,
    accessorFn: typeof accessor === 'function' ? accessor : undefined,
    header: Header,
    size: width,
    enableSorting: sortable,
    cell: Cell
      ? info => (
          <Cell value={info.getValue()} original={info.row.original} row={info.row.original} />
        )
      : undefined,
    meta: {
      className: classNames(...additionalClasses),
    },
  };
};

const useColumns = () =>
  React.useMemo(
    () =>
      [
        modelColumn,
        projectColumn,
        levelColumn,
        selColumn,
        sulColumn,
        authorColumn,
        createdDateColumn,
        updatedDateColumn,
        statusColumn,
        actionColumn,
      ].map(convertColumn),
    []
  );

// Legacy export for ModelUsageTable (still using react-table v6)
// This function wraps columns with default properties for react-table v6
export const columnsWithDefaults = (columns: Array<any>): Array<any> =>
  columns.map(column => ({
    ...column,
    resizable: false,
    sortable: true,
  }));

type SortingItem = { id: string, desc: boolean };

type Props = {|
  data: Model[],
  sorted: Array<SortingItem>,
  handleSortedChange: (sorted: Array<SortingItem>) => void,
|};

const ModelsTable = (props: Props): Element<*> => {
  const { data, sorted, handleSortedChange } = props;
  const columns = useColumns();

  // Apply custom row styling for archived, toggling, and recently created models
  const getRowClassName = React.useCallback((row: Model) => {
    if (row.togglingArchive || row.recentlyCreated) {
      return highlight;
    }
    return '';
  });

  return (
    <SortableTable
      data={data}
      columns={columns}
      initialSorting={sorted}
      className={classNames(['models-table', modelsTable])}
      getRowClassName={getRowClassName}
      tableId="models-table"
      onSortingChange={handleSortedChange}
      fixedHeight="700px"
    />
  );
};

export default ModelsTable;
